import React, { useEffect, useState } from 'react';
import {
  Search, Clock, MessageCircle, NotebookPen, Mic, BookOpen, Star, BadgeCheck,
  CirclePlay, Paperclip, Send, CheckCircle2
} from 'lucide-react';
import { Sparkle4 } from './BrandDecor';

const AUTO_ADVANCE_MS = 4500;

// Illustrative lesson cards for the "Browse" preview; categories filter them.
const mockLessons = [
  { title: 'Everyday Conversation Practice', meta: 'Live 1-on-1 · Beginner friendly', duration: '25 min', icon: MessageCircle, category: 'Conversation' },
  { title: 'Business English Essentials', meta: 'Live 1-on-1 · Intermediate', duration: '50 min', icon: NotebookPen, category: 'Business English' },
  { title: 'Grammar Made Simple', meta: 'Live 1-on-1 · All levels', duration: '25 min', icon: BookOpen, category: 'Grammar' },
  { title: 'Clear Pronunciation Coaching', meta: 'Live 1-on-1 · All levels', duration: '25 min', icon: Mic, category: 'Pronunciation' },
  { title: 'IELTS Speaking Prep', meta: 'Live 1-on-1 · Upper intermediate', duration: '50 min', icon: Star, category: 'Exam Prep' },
];
const categories = ['All lessons', 'Conversation', 'Business English', 'Grammar', 'Pronunciation', 'Exam Prep'];

const BrowsePreview = () => {
  const [category, setCategory] = useState('All lessons');
  const lessons = (category === 'All lessons' ? mockLessons : mockLessons.filter((l) => l.category === category)).slice(0, 2);
  return (
    <>
      <div className="flex items-center gap-2 rounded-lg bg-slate-100 dark:bg-white/5 px-3 py-2 text-xs text-text-muted mb-4">
        <Search className="w-3.5 h-3.5" />
        Search tutors, topics, or lesson types…
      </div>
      <div className="flex sm:hidden gap-1.5 overflow-x-auto no-scrollbar mb-3 -mx-1 px-1">
        {categories.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCategory(c)}
            className={`flex-shrink-0 rounded-full px-3 py-1 text-[11px] transition-colors ${c === category ? 'bg-primary text-white font-semibold' : 'bg-slate-100 dark:bg-white/5 text-text-secondary'}`}
          >
            {c}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[140px_minmax(0,1fr)] gap-4">
        <ul className="hidden sm:block space-y-1 text-xs">
          {categories.map((c) => (
            <li key={c}>
              <button
                type="button"
                onClick={() => setCategory(c)}
                className={`w-full text-left rounded-md px-2 py-1.5 transition-colors ${c === category ? 'bg-primary-100 text-primary font-semibold dark:bg-primary/30 dark:text-white' : 'text-text-secondary hover:bg-slate-100 dark:hover:bg-white/5'}`}
              >
                {c}
              </button>
            </li>
          ))}
        </ul>
        <div className="min-w-0 space-y-3">
          {lessons.map((l) => {
            const Icon = l.icon;
            return (
              <div key={l.title} className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-white/10 p-2.5 animate-in fade-in slide-in-from-bottom-1 duration-300">
                <div className="relative w-16 h-12 sm:w-24 sm:h-16 rounded-lg bg-gradient-to-br from-primary to-navy flex items-center justify-center flex-shrink-0">
                  <Icon className="w-6 h-6 text-gold-300" />
                  <span className="absolute bottom-1 right-1 rounded bg-black/60 text-white text-[9px] px-1">{l.duration}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-semibold text-navy dark:text-white truncate">{l.title}</p>
                  <p className="text-[11px] text-text-muted flex items-center gap-1 mt-0.5">
                    <Clock className="w-3 h-3" /> {l.meta}
                  </p>
                </div>
                <span className="hidden sm:inline-flex rounded-md border border-primary/40 text-primary dark:text-primary-300 text-xs font-semibold px-3 py-1">
                  Book
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

const SelectPreview = () => {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="flex flex-col sm:flex-row gap-4">
      <button
        type="button"
        onClick={() => setPlaying((p) => !p)}
        className="relative sm:w-48 h-32 rounded-xl bg-gradient-to-br from-primary to-navy flex items-center justify-center flex-shrink-0 overflow-hidden group"
        aria-label={playing ? 'Pause intro video' : 'Play intro video'}
      >
        {playing ? (
          <div className="flex items-end gap-1 h-10" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className="w-1.5 rounded-full bg-gold-300 animate-pulse" style={{ height: `${40 + ((i * 37) % 60)}%`, animationDelay: `${i * 120}ms` }} />
            ))}
          </div>
        ) : (
          <CirclePlay className="w-12 h-12 text-white/90 transition-transform group-hover:scale-110" />
        )}
        <span className="absolute bottom-2 left-2 rounded bg-black/60 text-white text-[10px] px-1.5 py-0.5">
          {playing ? 'Playing intro…' : 'Trial video · 1:30'}
        </span>
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-gold-500 text-navy font-bold flex items-center justify-center">MJ</div>
          <div className="min-w-0">
            <p className="font-semibold text-navy dark:text-white flex items-center gap-1">
              Maria J. <BadgeCheck className="w-4 h-4 text-primary dark:text-primary-300" />
            </p>
            <p className="text-[11px] text-text-muted">Conversation · Business English</p>
          </div>
        </div>
        <div className="flex items-center gap-0.5 mt-3" aria-label="Rated 5 out of 5">
          {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-4 h-4 fill-gold-500 text-gold-500" />)}
          <span className="ml-1 text-xs text-text-muted">5.0 · 120 lessons</span>
        </div>
        <p className="text-xs text-text-secondary mt-3 leading-relaxed">
          “Friendly, patient and focused on real-life speaking. Let's build your confidence together!”
        </p>
      </div>
    </div>
  );
};

const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const times = ['9:00 AM', '1:00 PM', '4:30 PM', '7:00 PM'];

const BookPreview = () => {
  const [day, setDay] = useState(1);
  const [time, setTime] = useState(null);
  return (
    <div>
      <div className="grid grid-cols-5 gap-2 mb-4">
        {days.map((d, i) => (
          <button
            key={d}
            type="button"
            onClick={() => { setDay(i); setTime(null); }}
            className={`rounded-lg py-2 text-xs font-semibold transition-all ${i === day ? 'bg-primary text-white shadow-card' : 'bg-slate-100 dark:bg-white/5 text-text-secondary hover:bg-primary-100 dark:hover:bg-white/10'}`}
          >
            {d}
            <span className="block text-[10px] font-normal opacity-80">{6 + i}</span>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 mb-4">
        {times.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTime(t)}
            className={`rounded-lg border px-3 py-2 text-xs font-medium transition-all ${t === time ? 'border-gold-500 bg-gold-500/15 text-navy dark:text-gold-300' : 'border-slate-200 dark:border-white/10 text-text-secondary hover:border-primary/50'}`}
          >
            {t}
          </button>
        ))}
      </div>
      <div className={`flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-all ${time ? 'bg-gold-500 text-navy' : 'bg-slate-100 dark:bg-white/5 text-text-muted'}`}>
        {time ? (<><CheckCircle2 className="w-4 h-4" /> Booked for {days[day]} at {time}</>) : 'Pick a time to book'}
      </div>
    </div>
  );
};

const chat = [
  { from: 'tutor', text: 'Great class today! Here are your notes 📄' },
  { from: 'student', text: 'Thank you! Can we practise interviews next time?' },
  { from: 'tutor', text: 'Absolutely — see you Thursday!' },
];

const ChatPreview = () => {
  const [shown, setShown] = useState(1);
  useEffect(() => {
    if (shown >= chat.length) return undefined;
    const id = setTimeout(() => setShown((n) => n + 1), 900);
    return () => clearTimeout(id);
  }, [shown]);
  return (
    <div>
      <div className="space-y-2 min-h-[150px]">
        {chat.slice(0, shown).map((m) => (
          <div key={m.text} className={`flex ${m.from === 'student' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}>
            <p className={`max-w-[80%] rounded-2xl px-3 py-2 text-xs leading-relaxed ${m.from === 'student' ? 'bg-primary text-white rounded-br-sm' : 'bg-slate-100 dark:bg-white/10 text-text-primary rounded-bl-sm'}`}>
              {m.text}
            </p>
          </div>
        ))}
        {shown < chat.length && (
          <div className="flex gap-1 px-3 py-2" aria-hidden="true">
            {[0, 1, 2].map((i) => <span key={i} className="w-1.5 h-1.5 rounded-full bg-text-muted animate-bounce" style={{ animationDelay: `${i * 150}ms` }} />)}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 mt-3 rounded-full border border-slate-200 dark:border-white/10 px-3 py-2">
        <Paperclip className="w-3.5 h-3.5 text-text-muted" />
        <span className="flex-1 text-xs text-text-muted">Type a message…</span>
        <button type="button" onClick={() => setShown(1)} className="text-primary dark:text-primary-300" aria-label="Replay chat">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const previews = [BrowsePreview, SelectPreview, BookPreview, ChatPreview];

// "How it works": clickable steps drive a live preview panel. Steps also
// auto-advance until the visitor interacts, then stay where they clicked.
const HowItWorks = ({ title, steps }) => {
  const [active, setActive] = useState(0);
  const [auto, setAuto] = useState(true);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    if (!auto || hovering) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    const id = setTimeout(() => setActive((a) => (a + 1) % steps.length), AUTO_ADVANCE_MS);
    return () => clearTimeout(id);
  }, [active, auto, hovering, steps.length]);

  const choose = (i) => { setActive(i); setAuto(false); };
  const Preview = previews[active];

  return (
    <section id="how-it-works" className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 relative scroll-mt-20">
      <div
        className="reveal max-w-[1600px] mx-auto liquid-glass rounded-[2rem] px-6 sm:px-10 py-12 bg-primary-50/60 dark:bg-transparent relative"
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        <Sparkle4 className="animate-twinkle hidden lg:block absolute top-8 left-[46%] w-8 h-8 text-navy/70 dark:text-gold-300" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="min-w-0">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-navy dark:text-white mb-2">{title}</h2>
            <span className="draw-line block h-1 w-20 rounded-full bg-gradient-to-r from-primary to-gold-400 mb-3" aria-hidden="true" />
            <p className="text-text-secondary mb-8">Four simple steps. One tutor who fits you. Tap a step to try it.</p>
            <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-3" role="tablist" aria-label="How it works steps">
              <span className="steps-connector hidden sm:block" aria-hidden="true"><span style={{ transform: `scaleX(${active / Math.max(1, steps.length - 1)})` }} /></span>
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = index === active;
                return (
                  <button
                    key={index}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => choose(index)}
                    data-testid={`step-${index}`}
                    className={`group relative text-center rounded-2xl px-2 py-4 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${isActive ? 'bg-white/80 dark:bg-white/10 shadow-card -translate-y-1' : 'hover:bg-white/50 dark:hover:bg-white/5 hover:-translate-y-0.5'}`}
                  >
                    <div className={`relative w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 transition-all duration-300 ${isActive ? 'bg-primary text-white scale-110 shadow-card-hover' : 'bg-white dark:bg-white/10 shadow-card text-primary dark:text-primary-300 group-hover:scale-105'}`}>
                      <Icon className="w-6 h-6" />
                      <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gold-500 text-navy text-[11px] font-bold flex items-center justify-center">
                        {index + 1}
                      </span>
                    </div>
                    <p className={`text-sm font-semibold leading-snug transition-colors ${isActive ? 'text-primary dark:text-gold-300' : 'text-navy dark:text-white'}`}>{step.title}</p>
                    <p className="text-xs text-text-secondary mt-1">{step.sub}</p>
                    {isActive && (
                      <span className="absolute left-4 right-4 bottom-1.5 h-0.5 rounded-full bg-primary/15 overflow-hidden" aria-hidden="true">
                        <span
                          key={`${active}-${auto && !hovering}`}
                          className="block h-full bg-gold-500 origin-left"
                          style={auto && !hovering ? { animation: `stepProgress ${AUTO_ADVANCE_MS}ms linear forwards` } : { width: '100%' }}
                        />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="min-w-0 rounded-2xl bg-white dark:bg-slate-900/80 shadow-card-hover border border-primary/10 p-3 sm:p-4" role="tabpanel" aria-live="polite">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-primary dark:text-primary-300">
                Step {active + 1} · {steps[active].title}
              </p>
              <div className="flex gap-1" aria-hidden="true">
                {steps.map((_, i) => (
                  <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === active ? 'w-5 bg-gold-500' : 'w-1.5 bg-slate-300 dark:bg-white/20'}`} />
                ))}
              </div>
            </div>
            <div key={active} className="animate-in fade-in slide-in-from-right-2 duration-300">
              <Preview />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
